import type { ReactNode } from "react";
import { Button, Input } from "@heroui/react";
import { SHARE_MAX, SHARE_MIN } from "../../lib/shares";
import { newCondition, ORDERS, setRuleOrder } from "../../lib/profilesRules";
import type {
  CallOrder,
  CallRule,
  PrimaryMode,
  ProfilePool,
  RuleCondition,
} from "../../lib/types";
import { Icon } from "../../ui/icons";
import { useStaffText } from "../../ui/StaffText";
import { MoveButtons } from "../recordParts";
import { Pick, type PickOption } from "../setupKit";
import { ConditionRow, PinLines, pinsUnder, type Pin } from "./ConditionRow";
import { OrderedPick } from "./OrderedPick";

export const RULES_MAX = 10;
const CONDITIONS_MAX = 5;
const PRIMARY: readonly PrimaryMode[] = ["ignore", "with", "without", "both"];
const POOL_PREFIX = "pool:";

/** A queue as the editor names it: its stub, then its name. */
export type QueueLabel = { id: string; letter: string; name: string };

export function QueueTag({ q }: { q: QueueLabel }) {
  return (
    <span className="pf-qtag">
      <span className="stub stub--sm">{q.letter}</span>
      <span>{q.name}</span>
    </span>
  );
}

/** Order, then shares when fair share, then primary resource: what Finally and every exception share. */
export function OrderFields({
  rule,
  shareQueues,
  singleAllowed,
  pins,
  readOnly,
  onChange,
}: {
  rule: CallRule;
  /** The queues the rule calls from, for the share boxes. */
  shareQueues: QueueLabel[];
  singleAllowed: boolean;
  pins: Pin[];
  readOnly: boolean;
  onChange: (next: CallRule) => void;
}) {
  const { say } = useStaffText();
  const badShare = Object.values(rule.shares ?? {}).some(
    (n) => !Number.isInteger(n) || n < SHARE_MIN || n > SHARE_MAX,
  );
  return (
    <div className="pf-rule__order">
      <div className="pf-rule__pair">
        <label className="field">
          <span>{say("profiles.wp.order")}</span>
          <Pick
            label={say("profiles.wp.order")}
            value={rule.order}
            disabled={readOnly}
            options={ORDERS.map((o) => ({
              value: o,
              label: say(`profiles.order.${o}`),
              disabled:
                o === "single" && !singleAllowed && rule.order !== "single",
            }))}
            onChange={(o) => onChange(setRuleOrder(rule, o as CallOrder))}
          />
        </label>
        <label className="field">
          <span>{say("profiles.wp.primary")}</span>
          <Pick
            label={say("profiles.wp.primary")}
            value={rule.primaryResource}
            disabled={readOnly}
            options={PRIMARY.map((m) => ({
              value: m,
              label: say(`profiles.primary.${m}`),
            }))}
            onChange={(m) =>
              onChange({ ...rule, primaryResource: m as PrimaryMode })
            }
          />
        </label>
      </div>
      <PinLines
        pins={pins}
        at={(f) => f === "order" || f === "primaryResource"}
      />
      {rule.order === "fair_share" && shareQueues.length > 0 && (
        <fieldset className="field">
          <legend>{say("profiles.wp.shares")}</legend>
          <ul className="picklist">
            {shareQueues.map((q) => (
              <li key={q.id} className="picklist__row">
                <span className="picklist__name">
                  <QueueTag q={q} />
                </span>
                <Input
                  className="pf-rule__share"
                  type="number"
                  inputMode="numeric"
                  aria-label={q.name}
                  min={SHARE_MIN}
                  max={SHARE_MAX}
                  disabled={readOnly}
                  value={rule.shares?.[q.id] ?? SHARE_MIN}
                  onChange={(e) =>
                    onChange({
                      ...rule,
                      shares: {
                        ...rule.shares,
                        [q.id]: Number(e.target.value) || 0,
                      },
                    })
                  }
                />
              </li>
            ))}
          </ul>
          {badShare && !pins.some((p) => p.field === "shares") && (
            <p className="field-error pf-wp__error">
              {say("profiles.problem.SHARE_RANGE")}
            </p>
          )}
          <PinLines pins={pins} at={(f) => f === "shares"} />
        </fieldset>
      )}
    </div>
  );
}

/** One Except-when rule: what it calls from, how, and the conditions that must all hold. */
export function RuleBlock({
  rule,
  index,
  count,
  queues,
  pools,
  pins,
  readOnly,
  onChange,
  onMove,
  onRemove,
}: {
  rule: CallRule;
  /** Its place in rules (1 = the first exception). */
  index: number;
  count: number;
  /** The profile's queues and pools, in the profile's order. */
  queues: QueueLabel[];
  pools: ProfilePool[];
  pins: Pin[];
  readOnly: boolean;
  onChange: (next: CallRule) => void;
  onMove: (by: number) => void;
  onRemove: () => void;
}) {
  const { say } = useStaffText();
  const title = say("profiles.wp.rule", { n: index });
  const ruleQueues = rule.queueIds
    .map((id) => queues.find((q) => q.id === id))
    .filter((q): q is QueueLabel => !!q);
  const conditions = rule.conditions ?? [];

  const items: { id: string; label: ReactNode; name: string }[] = [
    ...queues.map((q) => ({
      id: q.id,
      label: <QueueTag q={q} />,
      name: q.name,
    })),
    ...pools.map((p) => {
      const name = say(`profiles.wp.pool.${p}`);
      return {
        id: POOL_PREFIX + p,
        label: <span className="pf-qtag pf-qtag--pool">{name}</span>,
        name,
      };
    }),
  ];
  const picked = [
    ...rule.queueIds,
    ...(rule.pools ?? []).map((p) => POOL_PREFIX + p),
  ];
  const pick = (next: string[]) => {
    const queueIds = next.filter((id) => !id.startsWith(POOL_PREFIX));
    const nextPools = next
      .filter((id) => id.startsWith(POOL_PREFIX))
      .map((id) => id.slice(POOL_PREFIX.length) as ProfilePool);
    const out: CallRule = { ...rule, queueIds };
    if (nextPools.length > 0) out.pools = nextPools;
    else delete out.pools;
    if (out.shares) {
      const kept = Object.entries(out.shares).filter(([q]) =>
        queueIds.includes(q),
      );
      if (kept.length > 0) out.shares = Object.fromEntries(kept);
      else delete out.shares;
    }
    onChange(out);
  };

  const queueOptions: PickOption[] = queues.map((q) => ({
    value: q.id,
    label: q.name,
    note: q.letter,
  }));
  const setConditions = (next: RuleCondition[]) => {
    const out: CallRule = { ...rule, conditions: next };
    if (next.length === 0) delete out.conditions;
    onChange(out);
  };
  const condQueue = rule.queueIds[0] ?? queues[0]?.id ?? "";
  const condAt = (f: string) =>
    Number(/^conditions\[(\d+)\]/.exec(f)?.[1] ?? -1);
  // Whatever no field below shows is said at the foot of the block.
  const shownInside = (f: string) =>
    /^(queueIds|pools)(\[\d+\])?$/.test(f) ||
    f === "order" ||
    f === "primaryResource" ||
    f === "conditions" ||
    condAt(f) >= 0 ||
    (f === "shares" && rule.order === "fair_share" && ruleQueues.length > 0);

  return (
    <li className="pf-rule">
      <header className="pf-rule__head">
        <h4>{title}</h4>
        {!readOnly && (
          <>
            <MoveButtons
              name={title}
              first={index <= 1}
              last={index >= count - 1}
              onMove={onMove}
            />
            <Button
              isIconOnly
              variant="ghost"
              size="sm"
              aria-label={say("profiles.wp.removeItem", { name: title })}
              onPress={onRemove}
            >
              <Icon name="close" size={16} />
            </Button>
          </>
        )}
      </header>

      <fieldset className="field">
        <legend>{say("profiles.wp.callsFrom")}</legend>
        <OrderedPick
          all={items}
          picked={picked}
          onChange={pick}
          readOnly={readOnly}
          ordered={false}
        />
        <PinLines
          pins={pins}
          at={(f) => /^(queueIds|pools)(\[\d+\])?$/.test(f)}
        />
      </fieldset>

      <OrderFields
        rule={rule}
        shareQueues={ruleQueues}
        singleAllowed={picked.length <= 1}
        pins={pins}
        readOnly={readOnly}
        onChange={onChange}
      />

      <fieldset className="field">
        <legend>{say("profiles.wp.conditions")}</legend>
        {conditions.length > 0 && (
          <ol className="pf-rule__conds">
            {conditions.map((c, k) => (
              <ConditionRow
                key={k}
                condition={c}
                queues={queueOptions}
                pins={pinsUnder(pins, `conditions[${k}]`)}
                readOnly={readOnly}
                onChange={(next) =>
                  setConditions(
                    conditions.map((old, at) => (at === k ? next : old)),
                  )
                }
                onRemove={() =>
                  setConditions(conditions.filter((_, at) => at !== k))
                }
              />
            ))}
          </ol>
        )}
        <PinLines
          pins={pins}
          at={(f) => f === "conditions" || condAt(f) >= conditions.length}
        />
        {!readOnly && (
          <div className="picklist__add">
            <Button
              variant="outline"
              size="sm"
              isDisabled={
                conditions.length >= CONDITIONS_MAX || queues.length === 0
              }
              onPress={() =>
                setConditions([
                  ...conditions,
                  newCondition("wait_over", condQueue),
                ])
              }
            >
              <Icon name="plus" size={14} /> {say("profiles.wp.addCondition")}
            </Button>
          </div>
        )}
      </fieldset>
      <PinLines pins={pins} at={(f) => !shownInside(f)} />
    </li>
  );
}
